  import React from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Button,
  DropdownTrigger,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  User,
  Pagination,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Link,
  Select,
  SelectItem,
} from "@nextui-org/react";
import { PlusIcon } from "./PlusIcon";
import { VerticalDotsIcon } from "./VerticalDotsIcon";
import { SearchIcon } from "./SearchIcon";
import { ChevronDownIcon } from "./ChevronDownIcon";
import { incidentintegrationcolumn, incidentintegrationlist } from "../../data/data";
import { capitalize } from "../../utils/common";

const statusColorMap = {
  active: "success",
  paused: "danger",
  vacation: "warning",
};

const statusTimeMap = {
  earlyin: "success",
  earlyout: "danger",
};

const INITIAL_VISIBLE_COLUMNS = ["report_number", "department", "date_time", "reported_by", "incident_type", "witness", "actions"];

export default function IncidentIntegrationTable() {
  const [filterValue, setFilterValue] = React.useState("");
  const [selectedKeys, setSelectedKeys] = React.useState(new Set([]));
  const [visibleColumns, setVisibleColumns] = React.useState(new Set(INITIAL_VISIBLE_COLUMNS));
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [sortDescriptor, setSortDescriptor] = React.useState({
    column: "age",
    direction: "ascending",
  });
  const [page, setPage] = React.useState(1);

  const {
    isOpen: isLevelModalOpen,
    onOpen: onLevelModalOpen,
    onOpenChange: onLevelModalOpenChange,
  } = useDisclosure();

  const { isOpen: isApproverModalOpen, onOpen: onApproverModalOpen, onOpenChange: onApproverModalOpenChange } = useDisclosure();

  const hasSearchFilter = Boolean(filterValue);

  const headerColumns = React.useMemo(() => {
    if (visibleColumns === "all") return incidentintegrationcolumn;

    return incidentintegrationcolumn.filter((column) =>
      Array.from(visibleColumns).includes(column.uid)
    );
  }, [visibleColumns]);

  const filteredItems = React.useMemo(() => {
    let filteredUsers = [...incidentintegrationlist];

    if (hasSearchFilter) {
      filteredUsers = filteredUsers.filter((user) =>
        user.date.toLowerCase().includes(filterValue.toLowerCase())
      );
    }
    return filteredUsers;
  }, [hasSearchFilter, filterValue]);

  const pages = Math.ceil(filteredItems.length / rowsPerPage);

  const items = React.useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;

    return filteredItems.slice(start, end);
  }, [page, filteredItems, rowsPerPage]);

  const sortedItems = React.useMemo(() => {
    return [...items].sort((a, b) => {
      const first = a[sortDescriptor.column];
      const second = b[sortDescriptor.column];
      const cmp = first < second ? -1 : first > second ? 1 : 0;

      return sortDescriptor.direction === "descending" ? -cmp : cmp;
    });
  }, [sortDescriptor, items]);

  const renderCell = React.useCallback((user, columnKey) => {
    const cellValue = user[columnKey];

    switch (columnKey) {
      case "name":
        return (
          <User
            avatarProps={{ radius: "lg", src: user.avatar }}
            description={user.email}
            name={cellValue}
          >
            {user.email}
          </User>
        );
      case "sma_id":
        return (
          <p className="text-bold text-small whitespace-nowrap capitalize m-0">{user?.sma_id}</p>
        );

      case "surname":
        return (
          <p className="text-bold text-small whitespace-nowrap capitalize m-0">{user?.surname}</p>
        );
      case "given_name":
        return (
          <p className="text-bold text-small whitespace-nowrap capitalize m-0">{user?.given_name}</p>
        );
      case "rank":
        return (
          <p className="text-bold text-small whitespace-nowrap capitalize m-0">{user?.rank}</p>
        );
      case "vessel":
        return (
          <p className="text-bold text-small whitespace-nowrap capitalize m-0">{user?.vessel}</p>
        );
      case "qty":
        return (
          <p className="text-bold text-small whitespace-nowrap capitalize m-0">{user?.qty}</p>
        );
      case "sign_on_date":
        return (
          <p className="text-bold text-small whitespace-nowrap capitalize m-0">{user?.sign_on_date}</p>
        );
      case "amount":
        return (
          <p className="text-bold text-small whitespace-nowrap capitalize m-0">{user?.amount}</p>
        );
      case "actions":
        return (
          <div className="relative flex justify-center items-center gap-2">
            <Dropdown className="dark:bg-neutral-800">
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light">
                  <VerticalDotsIcon className="text-default-300" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem onPress={onLevelModalOpen}>Train</DropdownItem>
                <DropdownItem onPress={onApproverModalOpen}>View</DropdownItem>

              </DropdownMenu>
            </Dropdown>
          </div>
        );
      default:
        return cellValue;
    }
  },);

  const onRowsPerPageChange = React.useCallback((e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  }, []);

  const onSearchChange = React.useCallback((value) => {
    if (value) {
      setFilterValue(value);
      setPage(1);
    } else {
      setFilterValue("");
    }
  }, []);

  const onClear = React.useCallback(() => {
    setFilterValue("");
    setPage(1);
  }, []);

  const topContent = React.useMemo(() => {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex justify-between gap-3 items-end">
          <div className="flex gap-3">
            <Dropdown>
              <DropdownTrigger className="hidden sm:flex">
                <Button endContent={<ChevronDownIcon className="text-small" />} className="columnsBtn" variant="flat">
                  Columns
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                disallowEmptySelection
                aria-label="Table Columns"
                closeOnSelect={false}
                selectedKeys={visibleColumns}
                selectionMode="multiple"
                onSelectionChange={setVisibleColumns}
              >
                {incidentintegrationcolumn.map((column) => (
                  <DropdownItem key={column.uid} className="capitalize">
                    {capitalize(column.name)}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-default-400 text-small">Total {incidentintegrationlist.length} tickets</span>
          <label className="flex items-center text-default-400 text-small">
            Rows per page:
            <select
              className="bg-transparent outline-none text-default-400 text-small"
              onChange={onRowsPerPageChange}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
            </select>
          </label>
        </div>
      </div>
    );
  }, [onClear, filterValue, visibleColumns, onRowsPerPageChange, onSearchChange]);

  const bottomContent = React.useMemo(() => {
    return (
      <div className="py-2 px-2 flex justify-between items-center">
        <span className="w-[30%] text-small text-default-400">
          {selectedKeys === "all"
            ? "All items selected"
            : `${selectedKeys.size} of ${filteredItems.length} selected`}
        </span>
        <Pagination
          isCompact
          showControls
          showShadow
          id="pagination"
          color="secondary"
          page={page}
          total={pages}
          onChange={setPage}
        />
      </div>
    );
  }, [selectedKeys, filteredItems.length, page, pages]);

  return (
    <>
      <Table
        aria-label="Example table with custom cells, pagination and sorting"
        isHeaderSticky
        bottomContent={bottomContent}
        bottomContentPlacement="outside"
        classNames={{
          wrapper: "max-h-[382px]",
        }}
        sortDescriptor={sortDescriptor}
        topContent={topContent}
        topContentPlacement="outside"
        onSelectionChange={setSelectedKeys}
        onSortChange={setSortDescriptor}
      >
        <TableHeader columns={headerColumns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              align={column.uid === "actions" ? "center" : "start"}
              allowsSorting={column.sortable}
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody emptyContent={"No users found"} items={sortedItems}>
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Modal
        isOpen={isLevelModalOpen}
        placement="center"
        onOpenChange={onLevelModalOpenChange}
        style={{ maxWidth: "900px", width: "100%" }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Take Action</ModalHeader>
              <ModalBody style={{ maxHeight: "400px", overflowY: "auto" }}>
                <div className="flex gap-2 items-center">
                  <Input
                    variant="bordered"
                    label="Responsible Employee"
                    labelPlacement="outside"
                  />
                  <Select
                    label="Choose Training"
                    className="max-w-full"
                    labelPlacement="outside"
                    variant="bordered"
                  // onChange={(e) => setCourseType(e.target.value)} // Update course type state on change
                  >
                    <SelectItem key="Pharmacovigilance" value="Pharmacovigilance">
                      Pharmacovigilance
                    </SelectItem>
                    <SelectItem key="Pharmacology" value="Pharmacology">
                      Pharmacology
                    </SelectItem>
                    <SelectItem key="Toxicology" value="Toxicology">
                      Toxicology
                    </SelectItem>
                  </Select>
                </div>
              </ModalBody>
              <ModalFooter className="flex justify-center items-center gap-4">
                <Button color="danger" variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button className="incidentSubmitBtn" type="submit">
                  Submit
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>


      {/* --------------------------*****************************----------------------------------- */}


      <Modal
        isOpen={isApproverModalOpen}
        placement="center"
        onOpenChange={onApproverModalOpenChange}
        style={{ maxWidth: "900px", width: "100%" }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">View Incident Details</ModalHeader>
              <ModalBody style={{ maxHeight: "400px", overflowY: "auto" }}>
                {/* Add your Workflow Approver form content here */}
                <div className="form-row">
                  <p>Incident Report Number: 12345</p>
                  <p>Department: Chemistry</p>
                </div>
                <div className="form-row">
                  <p>Date: 2024-06-28</p>
                  <p>Time: 14:30</p>
                </div>
                <div className="form-row">
                  <p>Responsible Employee: John Doe</p>
                  <p>Reported By: Jane Smith</p>
                </div>
                <div className="form-row">
                  <p>Types of Incident: Spill</p>
                  <p>Attachment: <a href="#">View Attachment</a></p>
                </div>
                <div className="form-row">
                  <p>Detail Description: A chemical spill occurred in the lab.</p>
                </div>
                <div className="form-row">
                  <p>Damage Report: No significant damage reported.</p>
                </div>
              </ModalBody>

              <ModalHeader className="flex flex-col gap-1">Actions Taken</ModalHeader>
              <ModalBody style={{ maxHeight: "400px", overflowY: "auto" }}>
                <div className="form-row">
                  <p>Training: Spill response training conducted.</p>
                  <p>Witness: Mike Johnson</p>
                </div>
              </ModalBody>
              <ModalFooter className="flex justify-center items-center gap-4">
                <Button color="danger" variant="flat" onPress={onClose}>
                  Close
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>


    </>
  );
}
